import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useVenue } from "@/components/shared/VenueContext";
import { getMemToken } from "@/api/api";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Download, Trash2, FileText, Shield, Loader2, ExternalLink, Eye, ScrollText,
} from "lucide-react";
import moment from "moment";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function authFetch(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getMemToken() ? { Authorization: `Bearer ${getMemToken()}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Error" }));
    throw new Error(err.detail || "Request failed");
  }
  if (res.headers.get("content-type")?.includes("application/zip")) {
    return res.blob();
  }
  return res.json();
}

export default function RGPDCenter() {
  const { user, currentVenue, loading: venueLoading } = useVenue();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const isAdmin = user && (user.role === "superadmin" || user.role === "admin");

  const loadAuditLogs = async () => {
    if (!isAdmin || !currentVenue?.id) return;
    setLogsLoading(true);
    try {
      const params = user.role === "superadmin" ? "" : `?venue_id=${currentVenue.id}`;
      const data = await authFetch(`/api/rgpd/audit-logs${params}`);
      setAuditLogs(data.logs || []);
    } catch (err) {
      // Quietly fail if user can't see logs
      setAuditLogs([]);
    }
    setLogsLoading(false);
  };

  useEffect(() => {
    if (isAdmin && currentVenue) loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, currentVenue]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await authFetch("/api/rgpd/export-my-data");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hosys_data_export_${moment().format("YYYYMMDD")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dades exportades correctament");
    } catch (err) {
      toast.error(err.message);
    }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setShowDeleteDialog(false);
    try {
      await authFetch("/api/rgpd/delete-my-account", { method: "DELETE" });
      toast.success("Compte eliminat correctament. Sessió tancada.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      toast.error(err.message);
      setDeleting(false);
    }
  };

  if (venueLoading) return <QuickLoader />;

  return (
    <div className="space-y-5" data-testid="rgpd-page">
      <PageHeader
        title="Privacitat i RGPD"
        subtitle="Gestió dels teus drets segons el Reglament General de Protecció de Dades"
      />

      {/* Article 20 - Data Portability */}
      <Card className="p-5 border-0 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Exportar les meves dades (Article 20)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Descarrega un fitxer ZIP amb totes les dades personals que el sistema té associades al teu compte: registres de fitxatge, neteja, temperatures, incidències, etc. Format JSON estàndard.
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting}
              variant="outline"
              className="gap-2 rounded-xl"
              data-testid="export-data-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Descarregar el meu ZIP
            </Button>
          </div>
        </div>
      </Card>

      {/* Article 17 - Right to be Forgotten */}
      {user && user.role !== "superadmin" && (
        <Card className="p-5 border-0 shadow-sm bg-red-50">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Eliminar el meu compte (Article 17)</h3>
              <p className="text-sm text-red-800 mb-3">
                <strong>Atenció:</strong> Aquesta acció és irreversible. S'eliminarà el teu compte i s'anonimitzaran totes les teves dades personals.
                Els registres operatius (APPCC, neteja, temperatures) es conservaran de forma anònima per complir les obligacions legals d'auditoria sanitària.
              </p>
              <Button
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleting}
                variant="destructive"
                className="gap-2 rounded-xl"
                data-testid="delete-account-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar el meu compte
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Privacy Policy */}
      <Card className="p-5 border-0 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-gray-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Política de Privacitat</h3>
            <p className="text-sm text-gray-600 mb-3">
              Consulta com tractem les teves dades, qui les pot veure i quins drets tens.
            </p>
            <a href="/privacy-policy.pdf" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="gap-2 rounded-xl">
                <ExternalLink className="w-4 h-4" /> Veure política
              </Button>
            </a>
          </div>
        </div>
      </Card>

      {/* Audit Logs (admins only) */}
      {isAdmin && (
        <Card className="p-5 border-0 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Logs d'activitat</h3>
                  <p className="text-sm text-gray-600">Registre d'esdeveniments del local · accessos · canvis</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadAuditLogs}
                  className="gap-1.5"
                  data-testid="refresh-logs-btn"
                >
                  <Eye className="w-3.5 h-3.5" /> Actualitzar
                </Button>
              </div>

              {logsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : auditLogs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Cap log encara</p>
              ) : (
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {auditLogs.map((log, idx) => (
                    <div
                      key={`${log.ts || idx}-${log.action}-${log.user_email || ""}`}
                      className="text-xs font-mono p-2 bg-gray-50 rounded border border-gray-100"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">{log.action}</Badge>
                        <span className="text-gray-700">{log.user_email || "—"}</span>
                        {log.severity === "warning" && <Badge className="text-xs bg-amber-100 text-amber-700">⚠</Badge>}
                        <span className="text-gray-500 ml-auto">{moment(log.ts).format("DD/MM HH:mm:ss")}</span>
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <p className="text-gray-500 mt-0.5 truncate">
                          {JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estàs segur d'eliminar el compte?</AlertDialogTitle>
            <AlertDialogDescription>
              Aquesta acció <strong>no es pot desfer</strong>. El teu compte serà eliminat i les teves dades personals seran anonimitzades immediatament.
              Els registres operatius (APPCC, neteja, temperatures) es conservaran de forma anònima per complir les obligacions legals.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAccount} className="bg-red-600 hover:bg-red-700">
              Sí, eliminar-lo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
